import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Seo from '../components/Seo';

// Production-safe API host: same-origin in deployed builds, env var in preview.
const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API_HOST = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);

import { 
  BookOpen, Clock, DollarSign, CheckCircle, Award, 
  Play, ArrowRight, GraduationCap, FileText, Users,
  ArrowLeft, RefreshCw, Star, AlertCircle, XCircle
} from 'lucide-react';

const API_URL = API_HOST || '';


const OnlineCoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('browse');
  const [user, setUser] = useState(null);
  const [isMember, setIsMember] = useState(false);
  const [pnSyllabiOpen, setPnSyllabiOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    // Check for user
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      console.log('User loaded:', parsedUser);
      console.log('User role:', parsedUser?.role);
      setUser(parsedUser);
    }

    fetchCourses();
    
    // Check if returning from successful payment
    const courseId = searchParams.get('course_id');
    const sessionId = searchParams.get('session_id');
    if (courseId && sessionId && userData) {
      confirmEnrollment(sessionId, JSON.parse(userData));
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      fetchMyCourses();
      fetchMembershipStatus();
      // Express Enrollment: if a pending course intent was saved (e.g. user
      // clicked "Enroll Now" while logged-out, or just finished registering),
      // jump straight to Stripe checkout — one click, no extra friction.
      const urlEnroll = searchParams.get('enroll');
      const stored = localStorage.getItem('pending_enroll_course');
      const pendingCourseId = urlEnroll || stored;
      if (pendingCourseId) {
        localStorage.removeItem('pending_enroll_course');
        // Clear the ?enroll= param from the URL so refreshes don't re-trigger.
        if (urlEnroll) {
          window.history.replaceState({}, '', '/online-courses');
        }
        // Small delay so the user briefly sees the page before redirect.
        setTimeout(() => { handleEnroll(pendingCourseId); }, 200);
      }
    }
  }, [user]);

  const fetchMembershipStatus = async () => {
    if (!user) return;
    try {
      const response = await fetch(`${API_URL}/api/membership/status/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setIsMember(Boolean(data?.is_member));
      }
    } catch (error) {
      console.error('Error fetching membership status:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await fetch(`${API_URL}/api/online-courses`);
      const data = await response.json();
      setCourses(data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyCourses = async () => {
    if (!user) return;
    try {
      const response = await fetch(`${API_URL}/api/online-courses/my-courses/${user.id}`);
      const data = await response.json();
      setMyCourses(data);
    } catch (error) {
      console.error('Error fetching my courses:', error);
    }
  };

  const confirmEnrollment = async (sessionId, userData) => {
    try {
      const response = await fetch(`${API_URL}/api/online-courses/confirm-enrollment?session_id=${sessionId}&user_id=${userData.id}&user_email=${userData.email}&user_name=${userData.full_name}`, {
        method: 'POST'
      });

      if (response.ok) {
        // After a successful enrollment payment, drop the student straight
        // into the modules viewer for the course they just paid for so they
        // can start learning immediately. ?welcome=1 triggers the welcome modal.
        const paidCourseId = searchParams.get('course_id');
        if (paidCourseId) {
          window.location.href = `/online-course/${paidCourseId}?welcome=1`;
          return;
        }
        setActiveTab('my-courses');
        fetchMyCourses();
        // Clear URL params
        window.history.replaceState({}, '', '/online-courses');
      }
    } catch (error) {
      console.error('Error confirming enrollment:', error);
    }
  };

  const handleEnroll = async (courseId) => {
    if (!user) {
      // Express Enrollment: stash the course intent so once they log in,
      // they're routed straight to Stripe checkout — no second click needed.
      try { localStorage.setItem('pending_enroll_course', courseId); } catch (_e) { /* ignore */ }
      navigate(`/login?redirect=${encodeURIComponent(`/online-courses?enroll=${courseId}`)}`);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/online-courses/enroll`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          course_id: courseId,
          user_id: user.id,
          success_url: `${window.location.origin}/online-courses`,
          cancel_url: `${window.location.origin}/online-courses`
        })
      });

      const data = await response.json();
      if (data.checkout_url) {
        if (data.member_discount_applied) {
          // Brief notice before redirecting so member sees the savings.
          try {
            const saved = ((data.base_price_cents - data.amount_charged_cents) / 100).toFixed(2);
            // eslint-disable-next-line no-alert
            alert(`Member 10% discount applied — you save $${saved} on this enrollment.`);
          } catch (_e) { /* ignore */ }
        }
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      console.error('Error enrolling:', error);
    }
  };

  const handleAdminEnroll = async (courseId) => {
    if (!user || user.role !== 'admin') return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/online-courses/self-enroll/${courseId}?user_id=${user.id}&user_email=${encodeURIComponent(user.email)}&user_name=${encodeURIComponent(user.full_name || 'Admin')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.enrollment || data.message === 'Already enrolled') {
        alert(data.message || 'Enrolled successfully! You now have free access to this course.');
        fetchMyCourses();
      }
    } catch (error) {
      console.error('Error with admin enrollment:', error);
    }
  };

  const isEnrolled = (courseId) => {
    return myCourses.some(c => c.course_id === courseId);
  };

  const getCourseProgress = (courseId) => {
    const enrollment = myCourses.find(c => c.course_id === courseId);
    return enrollment;
  };

  // Collapse the 12 LPN/PN phase courses into ONE representative card.
  // Phase 1 (`lpn-prep`) drives enrollment; phases 2-12 unlock progressively
  // inside the Student Portal via the existing gating flow.
  const buildBrowseList = (allCourses) => {
    const phaseCourses = allCourses.filter(c => typeof c.id === 'string' && c.id.startsWith('lpn-') && (c.id === 'lpn-prep' || c.id.includes('phase-')));
    const phase1 = phaseCourses.find(c => c.id === 'lpn-prep');
    if (!phase1 || phaseCourses.length < 2) return allCourses;
    // Sort phases so 1 → 12.
    const phaseOrder = (c) => (c.id === 'lpn-prep' ? 1 : parseInt((c.id.match(/lpn-phase-(\d+)-prep/) || [])[1] || '99', 10));
    const orderedPhases = [...phaseCourses].sort((a, b) => phaseOrder(a) - phaseOrder(b));
    const otherCourses = allCourses.filter(c => !(typeof c.id === 'string' && c.id.startsWith('lpn-') && (c.id === 'lpn-prep' || c.id.includes('phase-'))));
    const grouped = {
      ...phase1,
      title: 'Practical Nurse Tech Apprenticeship Program — 12 Phases',
      description: 'A single, fully-bundled enrollment that unlocks all twelve sequential phases of MedCare\'s Practical Nurse Tech Apprenticeship Program. Begin with Phase 1 ($199 enrollment, $149 mid-term, $149 final = $497 collected). Each subsequent phase unlocks automatically inside your Student Portal once the prior phase\'s final exam is paid and passed. Total program covers patient-care fundamentals, pharmacology, medical-surgical, maternal/newborn, pediatrics, mental-health, geriatrics, community nursing, advanced pharmacology, leadership/delegation, and a comprehensive Phase 1 mastery review — plus 600 supervised clinical hours. Non-licensed PN Tech credential only.',
      duration: '12 Phases · Self-Paced',
      _isPnBundle: true,
      _phaseCount: phaseCourses.length,
      _phases: orderedPhases.map((p, idx) => ({
        phase_number: idx + 1,
        id: p.id,
        title: p.short_title || p.title,
        description: p.description,
        modules: (p.modules || []).map(m => m.title || m),
      })),
    };
    return [grouped, ...otherCourses];
  };

  const browseCourses = buildBrowseList(courses);
  // Filter by category chip + search term (title / description / category match).
  const CATEGORIES = ['All', 'Healthcare', 'Laboratory', 'Doctorate', 'Technology', 'Adult Education'];
  const normalisedQ = searchTerm.trim().toLowerCase();
  const filteredCourses = browseCourses.filter((c) => {
    // Category match (loose: doctorates show under 'Doctorate', etc.)
    const cat = (c.category || '').toLowerCase();
    const idLower = (c.id || '').toLowerCase();
    let inCat = activeCategory === 'All';
    if (!inCat) {
      if (activeCategory === 'Doctorate') inCat = idLower.includes('doctor') || idLower.includes('herbal') || idLower.includes('integrative');
      else if (activeCategory === 'Technology') inCat = idLower.includes('cyber') || idLower.includes('forklift') || idLower.includes('billing-coding') || idLower.includes('homeland');
      else if (activeCategory === 'Adult Education') inCat = idLower.includes('ged') || idLower.includes('medical-terminology');
      else inCat = cat === activeCategory.toLowerCase();
    }
    if (!inCat) return false;
    if (!normalisedQ) return true;
    return (
      (c.title || '').toLowerCase().includes(normalisedQ) ||
      (c.description || '').toLowerCase().includes(normalisedQ) ||
      (c.category || '').toLowerCase().includes(normalisedQ) ||
      (c.id || '').toLowerCase().includes(normalisedQ)
    );
  });
  const pnBundle = browseCourses.find(c => c._isPnBundle) || null;

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f8fafc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <RefreshCw size={48} style={{ animation: 'spin 1s linear infinite', color: '#1e40af' }} />
          <p style={{ marginTop: '16px', color: '#64748b' }}>Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <Seo
        title="Online Apprenticeship Courses — CMA, PN Tech, EMT, Phlebotomy & More · MedCare Memphis"
        description="Browse 27 DOL-registered apprenticeship and certificate programs. Self-paced online tuition with $99 phase pricing. CMA, Practical Nurse Tech, EMT, Phlebotomy, Medical Aide, Doctoral Naturopath, Homeland Security, GED and more."
        canonical="https://medcaretrainingacademy.com/online-courses"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "MedCare Online Apprenticeship Courses",
          "url": "https://medcaretrainingacademy.com/online-courses",
          "isPartOf": { "@id": "https://medcaretrainingacademy.com/#org" },
          "hasPart": (courses || []).slice(0, 30).map((c) => ({
            "@type": "Course",
            "name": c.title,
            "description": c.description,
            "url": `https://medcaretrainingacademy.com/online-courses/${c.id}`,
            "provider": { "@id": "https://medcaretrainingacademy.com/#org" }
          }))
        }}
      />
      {/* Header */}
      <header style={{ 
        background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)', 
        color: 'white', 
        padding: '24px 40px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Link to="/" style={{ color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <ArrowLeft size={20} />
                Back to Home
              </Link>
              <h1 style={{ margin: '0 0 8px 0', fontSize: '2rem' }}>
                <GraduationCap size={32} style={{ verticalAlign: 'middle', marginRight: '12px' }} />
                Online Courses
              </h1>
              <p style={{ margin: 0, opacity: 0.9 }}>
                Self-paced learning • Complete at your own pace • $199 enroll + $149 mid + $149 final (GED/Cyber/Forklift $99 flat) • Unlimited quiz retakes
              </p>
            </div>
            <div>
              {user ? (
                <div style={{ textAlign: 'right' }}>
                  <p style={{ margin: '0 0 4px', fontWeight: 'bold' }}>{user.full_name}</p>
                  {user.role === 'admin' && (
                    <span style={{ 
                      background: '#7c3aed', 
                      color: 'white', 
                      padding: '4px 12px', 
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      marginRight: '8px'
                    }}>
                      ADMIN MODE
                    </span>
                  )}
                  <Link to="/dashboard" style={{ color: '#fbbf24', fontSize: '0.875rem' }}>Go to Dashboard</Link>
                </div>
              ) : (
                <Link 
                  to="/login?redirect=/online-courses" 
                  data-testid="onlinecourses-header-student-login"
                  style={{ 
                    background: '#22c55e', 
                    color: 'white', 
                    padding: '10px 24px', 
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontWeight: 'bold'
                  }}
                >
                  Student Login
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div style={{ 
        background: 'white', 
        borderBottom: '1px solid #e5e7eb',
        padding: '0 40px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '32px' }}>
          <button
            onClick={() => setActiveTab('browse')}
            data-testid="browse-courses-tab"
            style={{
              padding: '16px 0',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === 'browse' ? '3px solid #1e40af' : '3px solid transparent',
              color: activeTab === 'browse' ? '#1e40af' : '#64748b',
              fontWeight: activeTab === 'browse' ? 'bold' : 'normal',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <BookOpen size={20} />
            Browse Courses
          </button>
          {user && (
            <button
              onClick={() => setActiveTab('my-courses')}
              data-testid="my-courses-tab"
              style={{
                padding: '16px 0',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'my-courses' ? '3px solid #1e40af' : '3px solid transparent',
                color: activeTab === 'my-courses' ? '#1e40af' : '#64748b',
                fontWeight: activeTab === 'my-courses' ? 'bold' : 'normal',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Award size={20} />
              My Courses ({myCourses.length})
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 40px' }}>
        
        {/* Browse Tab */}
        {activeTab === 'browse' && (
          <>
            {/* Features Banner */}
            <div style={{ 
              background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '32px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Clock size={24} color="#1e40af" />
                <div>
                  <p style={{ margin: 0, fontWeight: 'bold', color: '#1e3a5f' }}>Self-Paced Learning</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>Complete at your own pace</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <DollarSign size={24} color="#16a34a" />
                <div>
                  <p style={{ margin: 0, fontWeight: 'bold', color: '#1e3a5f' }}>Affordable Pricing</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>$497 standard · $99 flat for GED, Cyber, Forklift</p>
                </div>
              </div>
              {!user && (
                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  <Link to="/register" data-testid="onlinecourses-register-cta" style={{ background: '#10b981', color: 'white', padding: '10px 20px', borderRadius: 8, textDecoration: 'none', fontWeight: 700 }}>
                    Create Account & Enroll
                  </Link>
                  <Link to="/login" data-testid="onlinecourses-login-cta" style={{ background: 'white', color: '#1e3a8a', padding: '10px 20px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, border: '2px solid #1e3a8a' }}>
                    Student Login
                  </Link>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <RefreshCw size={24} color="#7c3aed" />
                <div>
                  <p style={{ margin: 0, fontWeight: 'bold', color: '#1e3a5f' }}>Unlimited Retakes</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>Until you pass</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Award size={24} color="#d97706" />
                <div>
                  <p style={{ margin: 0, fontWeight: 'bold', color: '#1e3a5f' }}>Certificate</p>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>Upon completion</p>
                </div>
              </div>
            </div>

            {/* Workforce-development disclaimer banner */}

            {/* Compact links — full disclaimers and refund policy live on dedicated pages */}
            <div data-testid="online-courses-policy-links" style={{
              background: '#f1f5f9',
              border: '1px solid #cbd5e1',
              borderRadius: '12px',
              padding: '14px 18px',
              marginBottom: '24px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '14px',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '0.9rem',
              color: '#334155',
            }}>
              <span>
                Before enrolling, please review the full
                <Link to="/disclaimers" style={{ color: '#0d9488', fontWeight: 700, textDecoration: 'underline', margin: '0 4px' }}>Disclaimers</Link>
                and the
                <Link to="/refund-policy" style={{ color: '#dc2626', fontWeight: 700, textDecoration: 'underline', margin: '0 4px' }}>Refund Policy</Link>.
              </span>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>All sales are final. Workforce-development apprenticeship.</span>
            </div>

            {/* Member discount banner */}
            {isMember && (
              <div data-testid="member-discount-banner" style={{
                background: 'linear-gradient(135deg,#0d9488,#9333ea)',
                color: '#ffffff',
                borderRadius: '12px',
                padding: '14px 18px',
                marginBottom: '24px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <span style={{ fontWeight: 700 }}>
                  <Star size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                  Association Member — 10% off every course enrollment auto-applied at checkout.
                </span>
                <span style={{ fontSize: '0.85rem', opacity: 0.92 }}>Discount applies to course enrollment only (not mid-term/final exams).</span>
              </div>
            )}
            {!isMember && user && (
              <div data-testid="join-member-cta-banner" style={{
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '12px',
                padding: '12px 18px',
                marginBottom: '24px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '10px',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '0.9rem',
                color: '#92400e',
              }}>
                <span>
                  <Star size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                  Save 10% on every course — join the Association for $9.99/mo.
                </span>
                <Link to="/association" data-testid="join-association-cta" style={{
                  background: '#9333ea', color: '#ffffff', padding: '8px 14px',
                  borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: '0.85rem',
                }}>Join Association</Link>
              </div>
            )}

            {/* Search & Category filter — easy course discovery */}
            <div data-testid="course-filter-bar" style={{
              background: 'white',
              borderRadius: 16,
              padding: '16px 20px',
              marginBottom: 24,
              boxShadow: '0 6px 14px rgba(15, 23, 42, 0.06)',
              border: '1px solid #e2e8f0',
            }}>
              <div style={{ position: 'relative', marginBottom: 14 }}>
                <input
                  type="search"
                  placeholder="Search programs by name, topic, or category..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="course-search-input"
                  style={{
                    width: '100%',
                    padding: '12px 16px 12px 44px',
                    border: '2px solid #e2e8f0',
                    borderRadius: 10,
                    fontSize: '0.95rem',
                    outline: 'none',
                    transition: 'border-color 0.15s ease',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#1e3a8a')}
                  onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                />
                <BookOpen size={18} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    data-testid={`course-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 999,
                      border: `2px solid ${activeCategory === cat ? '#1e3a8a' : '#cbd5e1'}`,
                      background: activeCategory === cat ? '#1e3a8a' : 'white',
                      color: activeCategory === cat ? 'white' : '#475569',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {cat}
                  </button>
                ))}
                {(searchTerm || activeCategory !== 'All') && (
                  <button
                    onClick={() => { setSearchTerm(''); setActiveCategory('All'); }}
                    data-testid="course-filter-clear"
                    style={{
                      padding: '8px 16px',
                      borderRadius: 999,
                      border: '2px solid #ef4444',
                      background: 'white',
                      color: '#ef4444',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      marginLeft: 'auto',
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
              <div style={{ marginTop: 12, fontSize: '0.8rem', color: '#64748b' }}>
                Showing <strong>{filteredCourses.length}</strong> of <strong>{browseCourses.length}</strong> programs
              </div>
            </div>

            {/* Course Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
              gap: '24px' 
            }}>
              {filteredCourses.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '48px 24px', background: 'white', borderRadius: 12, border: '1px dashed #cbd5e1' }}>
                  <p style={{ fontSize: '1.05rem', color: '#64748b', margin: 0 }}>No programs match your search. Try a different keyword or clear filters.</p>
                </div>
              ) : filteredCourses.map(course => {
                const enrolled = isEnrolled(course.id);
                const progress = getCourseProgress(course.id);
                
                return (
                  <div 
                    key={course.id}
                    data-testid={`course-card-${course.id}`}
                    style={{
                      background: 'white',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                      border: enrolled ? '2px solid #22c55e' : '1px solid #e5e7eb',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                  >
                    {/* Course Header */}
                    <div style={{ 
                      background: course.category === 'Healthcare' ? 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' :
                                 course.category === 'Laboratory' ? 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)' :
                                 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                      padding: '20px',
                      color: 'white'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <span style={{ 
                          background: 'rgba(255,255,255,0.2)', 
                          padding: '4px 10px', 
                          borderRadius: '20px',
                          fontSize: '0.75rem'
                        }}>
                          {course.category}
                        </span>
                        {enrolled && (
                          <span style={{ 
                            background: '#22c55e', 
                            padding: '4px 10px', 
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                            Enrolled
                          </span>
                        )}
                      </div>
                      <h3 style={{ margin: '12px 0 8px', fontSize: '1.25rem' }}>{course.title}</h3>
                      {course._isPnBundle && (
                        <div data-testid="pn-bundle-badge" style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          background: 'rgba(247,215,116,0.95)',
                          color: '#1e3a5f',
                          padding: '4px 10px',
                          borderRadius: 20,
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          marginBottom: 6,
                        }}>
                          <Star size={12} /> {course._phaseCount} Phases · One Enrollment Path
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '16px', fontSize: '0.875rem', opacity: 0.9 }}>
                        <span><Clock size={14} style={{ verticalAlign: 'middle' }} /> {course.duration}</span>
                        <span><BookOpen size={14} style={{ verticalAlign: 'middle' }} /> {course.daily_time}/day</span>
                      </div>
                    </div>

                    {/* Course Body */}
                    <div style={{ padding: '20px' }}>
                      <p style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '16px', lineHeight: '1.5' }}>
                        {course.description}
                      </p>

                      {/* Modules / Phases */}
                      <div style={{ marginBottom: '16px' }}>
                        {course._isPnBundle ? (
                          <>
                            <p style={{ fontWeight: 'bold', color: '#1e3a5f', marginBottom: '8px', fontSize: '0.875rem' }}>
                              All 12 Phases Included:
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {Array.from({ length: course._phaseCount }).map((_, i) => (
                                <span key={i} style={{
                                  background: i === 0 ? '#dcfce7' : '#f1f5f9',
                                  color: i === 0 ? '#166534' : '#475569',
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: i === 0 ? 700 : 500,
                                }}>
                                  Phase {i + 1}{i === 0 ? ' · Starts here' : ''}
                                </span>
                              ))}
                            </div>
                            <p style={{ marginTop: 10, fontSize: '0.78rem', color: '#64748b' }}>
                              Phases 2-12 unlock automatically inside your Student Portal once the prior phase&apos;s $149 final exam is paid and passed.
                            </p>
                            <button
                              type="button"
                              onClick={() => setPnSyllabiOpen(true)}
                              data-testid="view-pn-syllabi-btn"
                              style={{
                                marginTop: 10,
                                padding: '8px 12px',
                                background: 'transparent',
                                color: '#0d9488',
                                border: '1px solid #0d9488',
                                borderRadius: 8,
                                cursor: 'pointer',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                              }}
                            >
                              <FileText size={14} /> View all 12 phase syllabi
                            </button>
                          </>
                        ) : (
                          <>
                            <p style={{ fontWeight: 'bold', color: '#1e3a5f', marginBottom: '8px', fontSize: '0.875rem' }}>
                              {course.total_modules} Reading Modules:
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {course.modules?.slice(0, 3).map((module, idx) => (
                                <span key={idx} style={{
                                  background: '#f1f5f9',
                                  color: '#475569',
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem'
                                }}>
                                  {module.title}
                                </span>
                              ))}
                              {course.modules?.length > 3 && (
                                <span style={{
                                  background: '#e5e7eb',
                                  color: '#64748b',
                                  padding: '4px 10px',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem'
                                }}>
                                  +{course.modules.length - 3} more
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>

                      {/* Progress for enrolled */}
                      {progress && (
                        <div style={{ 
                          background: progress.completed_at ? '#f0fdf4' : '#fef3c7',
                          padding: '12px',
                          borderRadius: '8px',
                          marginBottom: '16px'
                        }}>
                          {progress.completed_at ? (
                            <p style={{ margin: 0, color: '#166534', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Award size={18} />
                              Course Complete! All {progress.total_modules} modules passed
                            </p>
                          ) : (
                            <p style={{ margin: 0, color: '#92400e', fontSize: '0.875rem' }}>
                              Progress: {progress.modules_completed || 0}/{progress.total_modules} modules completed
                            </p>
                          )}
                        </div>
                      )}

                      {/* Action Button */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {enrolled ? (
                          <Link
                            to={progress?.next_module_number
                              ? `/online-course/${course.id}?module=${progress.next_module_number}`
                              : `/online-course/${course.id}`}
                            data-testid={`start-course-${course.id}`}
                            style={{
                              flex: 1,
                              padding: '12px 20px',
                              background: progress?.completed_at ? '#22c55e' : '#1e40af',
                              color: 'white',
                              borderRadius: '8px',
                              textDecoration: 'none',
                              fontWeight: 'bold',
                              textAlign: 'center',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px'
                            }}
                          >
                            {progress?.completed_at ? (
                              <>
                                <Award size={18} />
                                View Certificate
                              </>
                            ) : (
                              <>
                                <Play size={18} />
                                Continue Learning
                              </>
                            )}
                          </Link>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#16a34a' }}>
                                {isMember ? (
                                  <>
                                    <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '1rem', fontWeight: 500, marginRight: 6 }}>
                                      ${course.price}
                                    </span>
                                    ${(course.price * 0.9).toFixed(2)}
                                    {course._isPnBundle ? ' / phase' : ''}
                                  </>
                                ) : (
                                  <>${course.price}{course._isPnBundle ? ' / phase' : ''}</>
                                )}
                              </span>
                              <button
                                onClick={() => handleEnroll(course.id)}
                                data-testid={`enroll-${course.id}`}
                                style={{
                                  padding: '12px 24px',
                                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontWeight: 'bold',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                              >
                                {course._isPnBundle ? 'Start Phase 1' : 'Enroll Now'}
                                <ArrowRight size={18} />
                              </button>
                            </div>
                            {/* Admin Free Access Button */}
                            {user?.role === 'admin' && (
                              <button
                                onClick={() => handleAdminEnroll(course.id)}
                                data-testid={`admin-enroll-${course.id}`}
                                style={{
                                  padding: '8px 16px',
                                  background: '#7c3aed',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px'
                                }}
                              >
                                🔓 Admin: Free Access
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* My Courses Tab */}
        {activeTab === 'my-courses' && (
          <>
            {myCourses.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                background: 'white',
                borderRadius: '16px'
              }}>
                <BookOpen size={64} color="#cbd5e1" style={{ marginBottom: '16px' }} />
                <h2 style={{ color: '#64748b', marginBottom: '8px' }}>No courses yet</h2>
                <p style={{ color: '#94a3b8', marginBottom: '24px' }}>
                  Browse our online courses and start learning today!
                </p>
                <button
                  onClick={() => setActiveTab('browse')}
                  style={{
                    padding: '12px 24px',
                    background: '#1e40af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Browse Courses
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '20px' }}>
                {myCourses.map(enrollment => (
                  <div 
                    key={enrollment.id}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '24px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '16px'
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <h3 style={{ margin: 0, color: '#1e3a5f' }}>
                          {enrollment.course_details?.title || enrollment.course_title}
                        </h3>
                        {enrollment.completed_at && (
                          <span style={{ 
                            background: '#dcfce7', 
                            color: '#166534',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}>
                            <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                            Completed
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '20px', fontSize: '0.875rem', color: '#64748b' }}>
                        <span>Apprentice ID: <strong>{enrollment.apprentice_id}</strong></span>
                        <span>Progress: <strong>{enrollment.modules_completed || 0}/{enrollment.total_modules} modules</strong></span>
                      </div>
                    </div>
                    <Link
                      to={`/online-course/${enrollment.course_id}`}
                      style={{
                        padding: '12px 24px',
                        background: enrollment.completed_at ? '#22c55e' : '#1e40af',
                        color: 'white',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {enrollment.completed_at ? (
                        <>
                          <Award size={18} />
                          View Certificate
                        </>
                      ) : (
                        <>
                          <Play size={18} />
                          Continue
                        </>
                      )}
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Workforce-development disclaimer */}

      {/* PN 12-Phase Syllabi Modal */}
      {pnSyllabiOpen && pnBundle && (
        <div
          onClick={() => setPnSyllabiOpen(false)}
          data-testid="pn-syllabi-modal-backdrop"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.65)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '40px 16px',
            overflowY: 'auto',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="pn-syllabi-modal"
            role="dialog"
            aria-modal="true"
            aria-label="PN 12-phase syllabi"
            style={{
              background: '#ffffff',
              borderRadius: 16,
              maxWidth: 880,
              width: '100%',
              boxShadow: '0 24px 60px -20px rgba(15,23,42,0.45)',
              overflow: 'hidden',
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg,#0d9488,#0f766e)',
              color: '#ffffff',
              padding: '20px 28px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 16,
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem' }}>Practical Nurse (PN) — 12-Phase Syllabi</h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.85rem', opacity: 0.9 }}>
                  Preview each phase. Phase 1 starts immediately on enrollment; phases 2-12 unlock as each prior final is paid &amp; passed.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPnSyllabiOpen(false)}
                data-testid="pn-syllabi-modal-close"
                aria-label="Close"
                style={{
                  background: 'rgba(255,255,255,0.18)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                <XCircle size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                Close
              </button>
            </div>
            <div style={{ padding: '20px 28px 28px', display: 'grid', gap: 14 }}>
              {(pnBundle._phases || []).map((p) => (
                <div
                  key={p.id}
                  data-testid={`pn-phase-syllabus-${p.phase_number}`}
                  style={{
                    border: '1px solid #e2e8f0',
                    borderLeft: p.phase_number === 1 ? '4px solid #16a34a' : '4px solid #0d9488',
                    borderRadius: 10,
                    padding: '14px 16px',
                    background: p.phase_number === 1 ? '#f0fdf4' : '#f8fafc',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, alignItems: 'baseline' }}>
                    <strong style={{ color: '#0f172a', fontSize: '1rem' }}>
                      Phase {p.phase_number} {p.phase_number === 1 ? '· Starts here' : ''}
                    </strong>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{p.id}</span>
                  </div>
                  <p style={{ margin: '6px 0 8px', color: '#334155', fontSize: '0.88rem', fontWeight: 600 }}>{p.title}</p>
                  <p style={{ margin: 0, color: '#475569', fontSize: '0.82rem', lineHeight: 1.5 }}>
                    {p.description ? (p.description.length > 320 ? p.description.slice(0, 317) + '…' : p.description) : 'Detailed syllabus available inside the Student Portal once this phase unlocks.'}
                  </p>
                  {p.modules && p.modules.length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {p.modules.slice(0, 6).map((m, i) => (
                        <span key={i} style={{
                          background: '#ffffff',
                          border: '1px solid #cbd5e1',
                          color: '#334155',
                          padding: '3px 8px',
                          borderRadius: 4,
                          fontSize: '0.72rem',
                        }}>{m}</span>
                      ))}
                      {p.modules.length > 6 && (
                        <span style={{ color: '#64748b', fontSize: '0.72rem', alignSelf: 'center' }}>+{p.modules.length - 6} more</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <div style={{ marginTop: 8, display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setPnSyllabiOpen(false)}
                  data-testid="pn-syllabi-cancel-btn"
                  style={{
                    background: '#e2e8f0',
                    color: '#334155',
                    border: 'none',
                    padding: '10px 16px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => { setPnSyllabiOpen(false); handleEnroll('lpn-prep'); }}
                  data-testid="pn-syllabi-start-phase1-btn"
                  style={{
                    background: 'linear-gradient(135deg,#22c55e,#16a34a)',
                    color: '#ffffff',
                    border: 'none',
                    padding: '10px 18px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  Start Phase 1 — ${isMember ? (pnBundle.price * 0.9).toFixed(2) : pnBundle.price}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer style={{ 
        background: '#1e3a5f', 
        color: 'white', 
        padding: '32px 40px',
        marginTop: '60px',
        textAlign: 'center'
      }}>
        <p style={{ margin: 0, opacity: 0.9 }}>
          MedCare Nursing Institute & Training Academy | (901) 625-1201
        </p>
        <p style={{ margin: '8px 0 0', opacity: 0.7, fontSize: '0.875rem' }}>
          All courses are offered online. Certificate of completion requires passing all module quizzes (mid-term &amp; final exams are $149 each on standard programs; GED, Cyber Security and Forklift OSHA are flat $99) and admin approval.
        </p>
      </footer>
    </div>
  );
};

export default OnlineCoursesPage;
